import React, { ReactNode } from 'react';
import { Task, UnallocatedInvestment } from './api';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import {
  User,
  getCurrentUserFullName,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { FormatUTC } from 'shared/utils/formatUTC';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { logError } from 'shared/utils/errorLogs';
import { numberWithCommas } from 'shared/utils/numberUtils';
import TrueImpactBadge from 'shared/design-system/components/TrueImpactBadge';
import { ReportAPIResponse } from '../ReportsBeta/api';
import { AllocationReportDatesFragment } from 'shared/apollo/fragments/allocation';
import { isDatePassed, toDate } from 'shared/utils/date';

export type TaskType = 'feedback' | 'finalUpdate' | 'interimUpdate' | 'others';

export type TaskReport = {
  [taskId: number]: {
    report: ReportAPIResponse;
    task: Task;
  };
};

export const getProgramName = (task: Task) => {
  const programName = task.allocation?.report?.program?.name;
  return (
    programName ||
    task.invitation?.investment?.programNameByDonor ||
    task.allocation?.investment.programNameByDonor ||
    'Unknown Program'
  );
};

export const getAllocationTaskDescription = (
  task: Task,
  programName?: string,
) => {
  const donationAmount = task.invitation?.investment?.donation;
  const portfolioYear = task?.invitation?.investment?.portfolio?.year;
  const funderName = task.invitation?.investment?.portfolio?.funder?.name;

  return (
    <>
      {donationAmount === undefined
        ? ''
        : `$${numberWithCommas(donationAmount)} `}
      from <strong>{funderName || 'Unknown Funder'}</strong> to {programName}{' '}
      {portfolioYear}
    </>
  );
};

export const getTaskMeta = (
  task: Task,
  currentUser?: User,
  userFullName?: string,
) => {
  const date = new FormatUTC(task.dueDate);
  const isCompleted = task.status === 'COMPLETED';
  const userName = `You (${userFullName ||
    getCurrentUserFullName(currentUser)})`;
  const completedBy = task?.invitation?.firstName
    ? `${task?.invitation?.firstName} ${task?.invitation?.lastName}`
    : userName;
  const displayName = isCompleted ? completedBy : userName;

  return (
    <TrueImpactText variant="body-small" className="task-meta">
      <div>
        {task.status === 'OVERDUE' ? (
          <div className="overdue-symbol">
            <FontAwesomeIcon icon={faTriangleExclamation} className="icon" />
            <strong color={COLORS.critical}>Overdue</strong>
          </div>
        ) : (
          <>
            <strong>Due:</strong> {date.formatDate()}
          </>
        )}
      </div>
      {(currentUser || userFullName) && (
        <div>
          <strong>{isCompleted ? 'Assigned to' : 'Sent to'}:</strong>{' '}
          {displayName}
        </div>
      )}
    </TrueImpactText>
  );
};

export const isLegacyTask = (task: Task): boolean => {
  const version = task.allocation?.report?.version;
  return version === null || version === 'v3';
};

export type ReportShape = {
  status: ReportAPIResponse['status'];
  endDate: string | null;
  startDate: string | null;
  allocations: {
    investment: { reportDate5?: string | null };
  }[];
};

export const isReportReadyForFinalUpdates = (report: ReportShape): boolean => {
  if (report.status === 'Final Updates Needed') {
    return true;
  }
  if (report.status !== 'Published') {
    return false;
  }

  return isDatePassed(report.endDate);
};

/**
 * Returns true if any allocation has a `reportDate5` that:
 *   - falls between the report's startDate and endDate
 *   - has already passed (is in the past)
 * AND the report's endDate has not yet passed.
 */
export const hasInterimDatePassed = (report: ReportShape): boolean => {
  const start = toDate(report.startDate);
  const end = toDate(report.endDate);
  if (!start || !end) return false;

  const now = new Date();
  if (end <= now) return false;

  return report.allocations.some(a => {
    const reportDate5 = toDate(a.investment.reportDate5);
    if (!reportDate5) return false;
    return reportDate5 > start && reportDate5 < end && reportDate5 <= now;
  });
};

export const isReportReadyForInterimUpdates = (
  report: ReportShape,
): boolean => {
  if (report.status === 'Interim Updates Needed') {
    return true;
  }
  if (report.status !== 'Published') {
    return false;
  }
  return hasInterimDatePassed(report);
};

const ShowLegacyTag: React.FC<{ title: string; task: Task }> = ({
  title,
  task,
}) => {
  const isV3 = isLegacyTask(task);
  if (isV3) {
    return (
      <>
        {title} <TrueImpactBadge>legacy</TrueImpactBadge>
      </>
    );
  }
  return <>{title}</>;
};

export const getTaskInfo = (
  task: Task,
  currentUser: User | undefined,
): { name: ReactNode; description: ReactNode; meta: ReactNode } => {
  const programName = getProgramName(task);
  switch (task.taskType) {
    case 'ALLOCATE':
      return {
        name: 'Allocate New Donation',
        description: getAllocationTaskDescription(task, programName),
        meta: getTaskMeta(task, currentUser),
      };
    case 'FINALIZE':
      return {
        name: <ShowLegacyTag title="Final Updates Needed" task={task} />,
        description: programName,
        meta: getTaskMeta(task),
      };
    case 'INTERIM_UPDATE':
      return {
        name: <ShowLegacyTag title="Interim Updates Needed" task={task} />,
        description: programName,
        meta: getTaskMeta(task),
      };
    case 'PUBLISH':
      return {
        name: <ShowLegacyTag title="Publish Report" task={task} />,
        description: programName,
        meta: getTaskMeta(task),
      };
    case 'RESOLVE':
      return {
        name: <ShowLegacyTag title="Resolve Report Feedback" task={task} />,
        description: programName,
        meta: getTaskMeta(task),
      };
    case 'SUBMIT':
      return {
        name: <ShowLegacyTag title="Submit Report" task={task} />,
        description: programName,
        meta: getTaskMeta(task),
      };
    case 'UPDATE':
      return {
        name: <ShowLegacyTag title="Update Report" task={task} />,
        description: programName,
        meta: getTaskMeta(task),
      };
    default:
      return {
        name: 'Action Required',
        description: programName,
        meta: getTaskMeta(task),
      };
  }
};

type GetStartUrlReturnType =
  | {
      error: undefined;
      path: string;
      hardRefresh: boolean;
      externalPath: string | undefined;
    }
  | {
      error: string;
    };

const v3UrlPath = 'https://ir.trueimpact.com';
const defaultErrorText =
  'Task must have an allocation. Unable to find report id for this task.';
export const getStartUrlForTask = (task: Task): GetStartUrlReturnType => {
  const externalPath = isLegacyTask(task) ? v3UrlPath : undefined;
  switch (task.taskType) {
    case 'ALLOCATE':
      return {
        error: undefined,
        hardRefresh: true,
        path: window.location.pathname + '#/task/' + task.taskId,
        externalPath: undefined,
      };

    case 'RESOLVE':
      if (task.allocation?.reportId) {
        return {
          error: undefined,
          hardRefresh: false,
          externalPath,
          path: '/npo/feedback/' + task.allocation?.reportId,
        };
      } else {
        logError(defaultErrorText, 'error', task);
        return {
          error: defaultErrorText,
        };
      }

    default:
    case 'PUBLISH':
    case 'SUBMIT':
    case 'UPDATE':
    case 'FINALIZE':
      if (task.allocation?.reportId) {
        if (task.allocation.investment?.portfolio?.isImpactSnapshot) {
          return {
            error: undefined,
            hardRefresh: false,
            externalPath: undefined,
            path: `/npo/programs-and-reports?programId=${task.allocation.report.programId}&reportId=${task.allocation.reportId}`,
          };
        }
        return {
          error: undefined,
          hardRefresh: false,
          externalPath,
          path: '/npo/report-builder/Impact/' + task.allocation?.reportId,
        };
      } else if (task.invitation?.investment?.allocations?.[0]?.reportId) {
        // Handle tasks with invitation instead of allocation
        if (task.invitation.investment.portfolio?.isImpactSnapshot) {
          return {
            error: undefined,
            hardRefresh: false,
            externalPath: undefined,
            path:
              `/npo/programs-and-reports?programId=${task.invitation.investment.allocations[0].report.programId}` +
              `&reportId=${task.invitation.investment.allocations[0].reportId}`,
          };
        }
        return {
          error: undefined,
          hardRefresh: false,
          externalPath,
          path:
            '/npo/report-builder/Impact/' +
            task.invitation.investment.allocations[0].reportId,
        };
      }
      logError(defaultErrorText, 'error', task);
      return {
        error: defaultErrorText,
      };
  }
};

const isPastDate = (dateString: string): boolean => {
  const inputDate = new Date(dateString);
  const currentDate = new Date();
  return inputDate < currentDate;
};

export const getTaskByReportId = (
  tasks: Task[],
): { [reportId: number]: Task | undefined } => {
  const result: { [reportId: number]: Task | undefined } = {};
  for (const task of tasks) {
    if (task.allocation?.reportId) {
      result[task.allocation.reportId] = task;
    }
  }
  return result;
};

export const createTaskFromReport = (
  task: Task | undefined,
  report: ReportAPIResponse & AllocationReportDatesFragment,
  type: 'feedback' | 'finalUpdate' | 'interimUpdate',
): Task => {
  const interimDueDate =
    type === 'interimUpdate'
      ? task?.allocation?.investment?.reportDate5 ??
        report.allocations?.[0]?.investment?.reportDate5 ??
        null
      : null;

  if (task) {
    const dueDate = interimDueDate ?? task.dueDate;
    return {
      ...task,
      dueDate,
      taskType:
        type === 'feedback'
          ? 'RESOLVE'
          : type === 'interimUpdate'
          ? 'INTERIM_UPDATE'
          : 'FINALIZE',
      completionDate: null,
      completionUser: null,
      status:
        task.status === 'COMPLETED'
          ? isPastDate(dueDate)
            ? 'OVERDUE'
            : 'UNCOMPLETED'
          : 'OVERDUE',
    };
  }
  const result: Task = {
    allocation: {
      reportId: report.dbId,
      investment: {
        dbId: report.dbId,
        donation: report.budget,
        programNameByDonor: report.name,
        tags: null,
        portfolio: {
          name: report.name || '',
          year: report.year.toString(),
          relationshipManager: {
            firstName: report.relationshipManagerFirstName || '',
            lastName: report.relationshipManagerLastName || '',
            email: report.relationshipManagerEmail || '',
          },
          funder: {
            name: report.donorName || '',
          },
        },
      },
      report: {
        dbId: report.dbId,
        version: report.version,
        year: report.year.toString(),
        endDate: report.endDate,
        enabled: true,
        programId: report.program?.Id,
        program: {
          id: report.program?.Id.toString(),
          name: report.program?.name,
          managerId: report.relationshipManagerId,
          manager:
            report.relationshipManagerEmail &&
            report.relationshipManagerFirstName &&
            report.relationshipManagerLastName
              ? {
                  email: report.relationshipManagerEmail,
                  firstName: report.relationshipManagerFirstName,
                  lastName: report.relationshipManagerLastName,
                }
              : null,
        },
        status: report.status,
      },
    },
    completionDate: null,
    completionUser: null,
    dueDate: interimDueDate ?? report.endDate,
    status: isPastDate(interimDueDate ?? report.endDate)
      ? 'OVERDUE'
      : 'UNCOMPLETED',
    taskId: report.dbId,
    taskType:
      type === 'feedback'
        ? 'RESOLVE'
        : type === 'interimUpdate'
        ? 'INTERIM_UPDATE'
        : 'FINALIZE',
    invitation: null,
  };
  return result;
};

export const buildUnallocatedInvestmentTasks = (
  unallocatedInvestments: UnallocatedInvestment[],
): Task[] => {
  return unallocatedInvestments.map(a => {
    const dueDate = a.invitation?.investment?.reportDate1 || a.dueDate || '';
    const status = isDatePassed(dueDate) ? 'OVERDUE' : 'UNCOMPLETED';
    return {
      taskId: a.dbId,
      allocation: a.allocations[0],
      invitation: a.invitation,
      completionDate: null,
      completionUser: null,
      dueDate,
      status,
      taskType: 'ALLOCATE',
    };
  });
};
